/**
 * Settings Layout Component
 *
 * Provides a consistent layout wrapper with tab navigation for all settings pages.
 * Uses feature gating to show all tabs to all users (TSK-020 pattern).
 *
 * @module components/layout/settings-layout
 *
 * @description
 * This layout component ensures visual consistency across the settings section by:
 * - Rendering a standard header with title and description
 * - Displaying tab navigation that highlights the active page
 * - Showing all tabs to all users with tier badges for gated features
 * - Opening upgrade modal when non-subscribed users click gated tabs
 * - Supporting horizontal scroll on mobile for tab overflow
 *
 * @example
 * // Usage in a settings page
 * export function SettingsPage() {
 *   return (
 *     <SettingsLayout>
 *       <div>Your settings content here</div>
 *     </SettingsLayout>
 *   );
 * }
 *
 * @see {@link SettingsPage} - Profile settings
 * @see {@link NotificationsSettingsPage} - Notification preferences
 * @see {@link BillingPage} - Subscription management
 */

import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { User, Key, CreditCard, Bell, Plug, Share2, Gift, UsersRound } from 'lucide-react';
import { cn } from '@/client/lib/utils';
import { useFeatures, type FeatureFlag } from '@/client/hooks/use-features';
import { TierBadge } from '@/client/components/feature-gate';
import { UpgradeModal } from '@/client/components/modals/upgrade-modal';

// =============================================================================
// Tab Configuration
// =============================================================================

/**
 * Settings tab definition
 */
interface SettingsTab {
  name: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  /** If set, tab is gated to this feature and shows upgrade modal for non-subscribers */
  featureFlag?: FeatureFlag;
  /** Custom upgrade message for the modal */
  upgradeMessage?: string;
}

/**
 * Settings navigation tabs configuration
 * All tabs are always visible - gated tabs show tier badges and open upgrade modal
 */
const tabs: SettingsTab[] = [
  { name: 'Profile', href: '/settings', icon: User },
  { name: 'Notifications', href: '/settings/notifications', icon: Bell },
  { name: 'Integrations', href: '/settings/integrations', icon: Plug },
  {
    name: 'Sharing',
    href: '/settings/sharing',
    icon: Share2,
    featureFlag: 'link_sharing',
    upgradeMessage: 'Share link analytics and collaborate with your team',
  },
  {
    name: 'Team',
    href: '/settings/team',
    icon: UsersRound,
    featureFlag: 'team_members',
    upgradeMessage: 'Invite team members to collaborate on your workspace',
  },
  { name: 'API Tokens', href: '/settings/tokens', icon: Key },
  { name: 'Billing', href: '/settings/billing', icon: CreditCard },
  { name: 'Referrals', href: '/settings/referrals', icon: Gift },
];

// =============================================================================
// Component Props
// =============================================================================

/**
 * Props for the SettingsLayout component
 * @interface SettingsLayoutProps
 */
interface SettingsLayoutProps {
  /** Child content to render within the layout */
  children: React.ReactNode;
}

// =============================================================================
// Main Component
// =============================================================================

/**
 * Individual settings tab with feature gating support
 */
function SettingsTabItem({ tab, isActive }: { tab: SettingsTab; isActive: boolean }) {
  const { hasFeature, getRequiredTier } = useFeatures();
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  const isGated = tab.featureFlag && !hasFeature(tab.featureFlag);
  const requiredTier = tab.featureFlag ? getRequiredTier(tab.featureFlag) : 'free';

  const handleClick = (e: React.MouseEvent) => {
    if (isGated) {
      e.preventDefault();
      setShowUpgradeModal(true);
    }
  };

  return (
    <>
      <Link
        to={tab.href}
        onClick={handleClick}
        className={cn(
          'flex items-center gap-2 px-1 py-3 border-b-2 text-sm font-medium transition-colors whitespace-nowrap',
          isActive
            ? 'border-primary text-primary'
            : 'border-transparent text-muted-foreground hover:text-foreground',
          isGated && 'opacity-75'
        )}
      >
        <tab.icon className="h-4 w-4" />
        {tab.name}
        {isGated && <TierBadge tier={requiredTier} size="xs" />}
      </Link>

      {/* Upgrade modal for gated tabs */}
      {tab.featureFlag && (
        <UpgradeModal
          open={showUpgradeModal}
          onOpenChange={setShowUpgradeModal}
          feature={tab.featureFlag}
          message={tab.upgradeMessage}
        />
      )}
    </>
  );
}

/**
 * SettingsLayout Component
 *
 * Wraps settings pages with consistent header and tab navigation.
 * All tabs are visible to all users - gated tabs show tier badges
 * and open upgrade modal on click.
 *
 * @component
 * @param {SettingsLayoutProps} props - Component props
 * @returns {JSX.Element} Settings layout with tab navigation
 */
export function SettingsLayout({ children }: SettingsLayoutProps) {
  const location = useLocation();

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Settings</h1>
        <p className="text-muted-foreground">Manage your account settings.</p>
      </div>

      {/* Tabs - all visible, gated tabs show badges and open upgrade modal */}
      <div className="border-b">
        <nav className="flex gap-4 overflow-x-auto scrollbar-none">
          {tabs.map((tab) => (
            <SettingsTabItem key={tab.href} tab={tab} isActive={location.pathname === tab.href} />
          ))}
        </nav>
      </div>

      {/* Page Content */}
      {children}
    </div>
  );
}
